import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, methodologyService, Methodology, GuidedMethodology } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import FeaturedMethodology from "../../components/ui/FeaturedMethodology";
import FeaturedToolkit from "../../components/ui/FeaturedToolkit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { getInvitationsForUser } from '../../lib/huntersBoardService';
import { hackerChecklistService, HackerChecklist } from '../../lib/hackerChecklistsService';
import NavBar from '../../components/NavBar';

interface MenuItem {
  title: string;
  delay: number;
  path: string;
  icon?: string;
}

interface LastSession {
  methodologyId: string;
  subMethodologyId?: string;
  lastAccessed: string;
  title: string;
  type?: 'straight-up' | 'guided';
}

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  type: 'straight-up' | 'guided';
  path: string;
}

export const DashboardPage = (): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [methodologies, setMethodologies] = useState<Methodology[]>([]);
  const [guidedMethodologies, setGuidedMethodologies] = useState<GuidedMethodology[]>([]);
  const [hackerChecklists, setHackerChecklists] = useState<HackerChecklist[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastSession, setLastSession] = useState<LastSession | null>(null);
  const [greeting, setGreeting] = useState<string>("Welcome back");
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  
  // Search functionality
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);

  // Authentication effect
  const [inviteCount, setInviteCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      setSessionLoading(false);
      return () => {};
    }

    // Check if we have a stored username first
    const storedName = localStorage.getItem('rawpa_user_name');
    if (storedName) {
      setUserName(storedName);
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Only set username from auth if we don't already have one in localStorage
        if (!storedName) {
          const displayName = user.displayName || user.email?.split('@')[0] || 'User';
          setUserName(displayName);
          localStorage.setItem('rawpa_user_name', displayName);
        }
        
        // Check for last session
        checkForLastSession();
        setUserId(user.uid);
      } else {
        navigate('/signin');
        setSessionLoading(false);
        setUserId(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!userId) return;
    getInvitationsForUser(userId).then(invites => {
      setInviteCount(invites.length);
    });
  }, [userId]);

  // Load guided methodologies for the menu
  useEffect(() => {
    // Skip if guided methodologies are disabled
    if (settings.disableGuidedMethodologies) {
      return;
    }
    
    const loadGuidedMethodologies = async () => {
      try {
        const data = await methodologyService.getGuidedMethodologies();
        setGuidedMethodologies(data.filter(m => m.status === 'Active'));
      } catch (error) {
        console.error("Error loading guided methodologies:", error);
      }
    };

    loadGuidedMethodologies();
  }, [settings.disableGuidedMethodologies]);

useEffect(() => {
  if (settings.disableHackerChecklists) {
    setHackerChecklists([]);
    return;
  }

  const unsubscribe = hackerChecklistService.subscribeToChecklists((data) => {
    setHackerChecklists(data);
  }, { status: 'Published' });

  return () => {
    if (typeof unsubscribe === 'function') unsubscribe();
  };
}, [settings.disableHackerChecklists]);

  // Check if user has any completed content items
  const checkForLastSession = async () => {
    try {
      // Small delay to ensure Firebase is fully initialized
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Clean up any invalid session references first
      await methodologyService.cleanupInvalidSessions();
      
      // Find methodologies with completed items
      const methodologiesWithCompletedItems = await methodologyService.findMethodologiesWithCompletedItems();
      
      if (methodologiesWithCompletedItems) {
        console.log(`Last session found - Type: ${methodologiesWithCompletedItems.type || 'straight-up'}, ID: ${methodologiesWithCompletedItems.methodologyId}`);
        
        // Check if the methodology type is disabled
        if ((methodologiesWithCompletedItems.type === 'guided' && settings.disableGuidedMethodologies) ||
            (methodologiesWithCompletedItems.type === 'straight-up' && settings.disableStraightUpMethodologies) ||
            (!methodologiesWithCompletedItems.type && settings.disableStraightUpMethodologies)) {
          // Skip setting last session if the methodology type is disabled
          setSessionLoading(false);
          return;
        }
        
        // Set the session with the correct type
        setLastSession({
          ...methodologiesWithCompletedItems,
          // Only use default 'straight-up' if type is undefined
          type: methodologiesWithCompletedItems.type || 'straight-up'
        });
        
        // Set a random greeting for users with existing sessions
        const sessionGreetings = [
          "Let's hop back on that session",
          "Ready to continue where you left off",
          "Your session is waiting for you",
          "Pick up right where you left off"
        ];
        const randomIndex = Math.floor(Math.random() * sessionGreetings.length);
        setGreeting(sessionGreetings[randomIndex]);
      } else {
        // Set a random greeting for new users
        const newUserGreetings = [
          "Welcome back",
          "Great to see you again",
          "Hello there"
        ];
        const randomIndex = Math.floor(Math.random() * newUserGreetings.length);
        setGreeting(newUserGreetings[randomIndex]);
      }
      
      // Mark session loading as complete
      setSessionLoading(false);
    } catch (error) {
      console.error("Error checking for last session:", error);
      setSessionLoading(false);
    }
  };

  // Load methodologies
  useEffect(() => {
    console.group('DashboardPage - Methodology Subscription');
    console.log("Setting up methodologies subscription in DashboardPage");
    setLoading(true);
    
    const unsubscribe = methodologyService.subscribeToMethodologies((updatedMethodologies) => {
      console.log("Dashboard received updated methodologies count:", updatedMethodologies.length);
      
      // Check for sub-methodologies
      let subMethodologiesCount = 0;
      updatedMethodologies.forEach(m => {
        if (m.subMethodologies && m.subMethodologies.length > 0) {
          console.log(`Methodology ${m.id} (${m.title}) has ${m.subMethodologies.length} sub-methodologies:`, 
            m.subMethodologies.map(sm => sm.title).join(', '));
          subMethodologiesCount += m.subMethodologies.length;
        }
      });
      
      console.log(`Total sub-methodologies: ${subMethodologiesCount}`);
      setMethodologies(updatedMethodologies);
      setLoading(false);
    });

    return () => {
      console.log("Cleaning up methodologies subscription in DashboardPage");
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      console.groupEnd();
    };
  }, []);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      } else {
        setError("Authentication service unavailable");
      }
    } catch (error) {
      console.error('Error signing out:', error);
      setError("Failed to sign out. Please try again.");
    }
  };

  const handleContinueSession = async () => {
    if (lastSession) {
      try {
        console.log(`Continuing session - Type: ${lastSession.type}, ID: ${lastSession.methodologyId}, SubID: ${lastSession.subMethodologyId || 'N/A'}`);
        
        // Check if the methodology type is disabled
        if ((lastSession.type === 'guided' && settings.disableGuidedMethodologies) ||
            (lastSession.type === 'straight-up' && settings.disableStraightUpMethodologies) ||
            (lastSession.methodologyId === 'pathway' && settings.disablePathway)) {
          setError(`${lastSession.type === 'guided' ? 'Guided' : lastSession.type === 'straight-up' ? 'Straight-up' : 'Pathway'} methodologies are currently disabled by the administrator.`);
          return;
        }
        
        // Verify the methodology exists before navigating
        if (lastSession.type === 'guided') {
          console.log("Continuing guided methodology session");
          // Check if guided methodology exists
          const guidedMethodology = await methodologyService.getGuidedMethodologyById(lastSession.methodologyId);
          if (!guidedMethodology) {
            setError("The previous session's methodology is no longer available");
            return;
          }
          navigate(`/guided-methodologies/${lastSession.methodologyId}`);
        } else {
          console.log("Continuing straight-up methodology session");
          
          // Special handling for pathway methodology
          if (lastSession.methodologyId === 'pathway') {
            navigate(`/methodology/pathway`);
            return;
          }
          
          // Check if straight-up methodology exists
          const methodology = await methodologyService.getMethodologyById(lastSession.methodologyId);
          if (!methodology) {
            setError("The previous session's methodology is no longer available");
            return;
          }
          
          if (lastSession.subMethodologyId) {
            navigate(`/methodology/${lastSession.methodologyId}/${lastSession.subMethodologyId}`);
          } else {
            navigate(`/methodology/${lastSession.methodologyId}`);
          }
        }
      } catch (error) {
        console.error("Error continuing session:", error);
        setError("Failed to continue your session. Please try again.");
      }
    }
  };

  // Handle search
  const handleSearch = (query: string): void => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    
    // Filter straight-up methodologies (exclude any that might be disabled)
    const straightUpResults = !settings.disableStraightUpMethodologies 
      ? methodologies
          .filter(m => 
            // Filter out pathway if disabled
            !(m.id === 'pathway' && settings.disablePathway) &&
            // Search in title, description, or keywords
            (m.title.toLowerCase().includes(lowerQuery) || 
             (m.description && m.description.toLowerCase().includes(lowerQuery)) ||
             (m.keywords && m.keywords.some(k => k.toLowerCase().includes(lowerQuery))))
          )
          .map(m => ({
            id: m.id,
            title: m.title,
            description: m.description,
            type: 'straight-up' as const,
            path: `/methodology/${m.id}`
          }))
      : [];
    
    // Filter guided methodologies (exclude drafts, inactive, or if disabled)
    const guidedResults = !settings.disableGuidedMethodologies
      ? guidedMethodologies
          .filter(m => 
            // Only include active methodologies (filter out drafts and inactive)
            m.status === 'Active' &&
            // Search in name, description, or keywords
            (m.name.toLowerCase().includes(lowerQuery) || 
             (m.description && m.description.toLowerCase().includes(lowerQuery)) ||
             (m.keywords && m.keywords.some(k => k.toLowerCase().includes(lowerQuery))))
          )
          .map(m => ({
            id: m.id,
            title: m.name,
            description: m.description,
            type: 'guided' as const,
            path: `/guided-methodologies/${m.id}`
          }))
      : [];
    
    // Combine results
    setSearchResults([...straightUpResults, ...guidedResults]);
  };

  // Check if all methodologies are disabled
  const areAllMethodologiesDisabled = 
    settings.disableStraightUpMethodologies && 
    settings.disableGuidedMethodologies && 
    settings.disablePathway;

  // Show loading state if either main loading or session loading is true
  if (loading || sessionLoading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  // Create menu items from methodologies
  const menuItems: MenuItem[] = [];

  const getMenuDelay = (index: number, base: number = 0) => {
    const delay = base + index * 0.05;
    return Math.min(delay, 0.6);
  };
  
  // Helper function to check if a methodology ID is actually a guided methodology
  const isGuidedMethodology = (methodologyId: string): boolean => {
    return guidedMethodologies.some(gm => gm.id === methodologyId);
  };

  // Special case for guided methodology that's stored in methodologies collection
  const isSpecialGuidedMethodology = (methodologyId: string): boolean => {
    return methodologyId === 'ib14w56wb98EUUlMQwRN';
  };

  // Add the three main methodologies by finding them by title
  if (!settings.disableStraightUpMethodologies) {
    // Find Straight-up methodology by title
    const straightUp = methodologies.find(m => 
      m.title.toLowerCase().includes('straight-up') || 
      m.title.toLowerCase().includes('straight up')
    );
    if (straightUp && !isGuidedMethodology(straightUp.id)) {
      menuItems.push({
        title: straightUp.title,
        delay: 0,
        path: `/methodology/${straightUp.id}?newSession=true`
      });
    }
  }
  
  if (!settings.disableGuidedMethodologies) {
    // Find Guided methodology by title
    const guided = methodologies.find(m => 
      m.title.toLowerCase().includes('guided') || 
      m.title.toLowerCase().includes('step by step')
    );
    if (guided) {
      // Check if this is actually a guided methodology
      if (isGuidedMethodology(guided.id) || isSpecialGuidedMethodology(guided.id)) {
        // Route to guided-methodologies list page
        menuItems.push({
          title: guided.title,
          delay: 0,
          path: `/guided-methodologies?newSession=true`
        });
      } else {
        // Route to regular methodology path
        menuItems.push({
          title: guided.title,
          delay: 0,
          path: `/guided-methodologies?newSession=true`
        });
        
        // Add sub-methodologies for guided methodology
        if (guided.subMethodologies && guided.subMethodologies.length > 0) {
          guided.subMethodologies.forEach((subMethodology, index) => {
            menuItems.push({
              title: `  ${subMethodology.title}`,
              delay: 0.1 + (index * 0.05),
              path: `/methodology/${guided.id}/${subMethodology.id}?newSession=true`
            });
          });
        }
      }
    }
  }
  
  if (!settings.disablePathway) {
    // Find Pathway methodology by title
    const pathway = methodologies.find(m => 
      m.title.toLowerCase().includes('pathway') || 
      m.title.toLowerCase().includes('structured learning')
    );
    if (pathway) {
      // Always use hard-coded route for pathway methodology
      menuItems.push({
        title: pathway.title,
        delay: 0,
        path: `/methodology/pathway?newSession=true`
      });
    } else {
      // Fallback to hard-coded route if not found by title
      menuItems.push({
        title: "Pathway Methodology",
        delay: 0,
        path: `/methodology/pathway?newSession=true`
      });
    }
  }

  if (!settings.disableHackerChecklists && hackerChecklists.length > 0) {
    const baseDelay = getMenuDelay(menuItems.length);
    menuItems.push({
      title: 'Hacker Checklists',
      delay: baseDelay,
      path: '/hacker-checklists?newSession=true'
    });

    hackerChecklists.slice(0, 6).forEach((checklist, index) => {
      menuItems.push({
        title: `Checklist: ${checklist.title}`,
        delay: getMenuDelay(index + 1, baseDelay),
        path: `/hacker-checklists/${checklist.id}?newSession=true`
      });
    });
  }

  // Fallback: If we couldn't find the methodologies by title, use hard-coded routes
  if (menuItems.length === 0) {
    
    if (!settings.disableStraightUpMethodologies) {
      menuItems.push({
        title: "Straight-up Methodology",
        delay: 0,
        path: `/methodology/straight-up?newSession=true`
      });
    }
    
    if (!settings.disableGuidedMethodologies) {
      menuItems.push({
        title: "Guided Methodology",
        delay: 0,
        path: `/guided-methodologies?newSession=true`
      });
      
      // Add guided methodologies as sub-items
      guidedMethodologies.forEach((guidedMethodology, index) => {
        menuItems.push({
          title: `  ${guidedMethodology.name}`,
          delay: getMenuDelay(index, 0.1),
          path: `/guided-methodologies/${guidedMethodology.id}?newSession=true`
        });
      });
    }
    
    if (!settings.disablePathway) {
      menuItems.push({
        title: "Pathway Methodology",
        delay: 0,
        path: `/methodology/pathway?newSession=true`
      });
    }

    if (!settings.disableHackerChecklists && hackerChecklists.length > 0) {
      const baseDelay = getMenuDelay(menuItems.length);
      menuItems.push({
        title: 'Hacker Checklists',
        delay: baseDelay,
        path: '/hacker-checklists?newSession=true'
      });
      hackerChecklists.slice(0, 6).forEach((checklist, index) => {
        menuItems.push({
          title: `Checklist: ${checklist.title}`,
          delay: getMenuDelay(index + 1, baseDelay),
          path: `/hacker-checklists/${checklist.id}?newSession=true`
        });
      });
    }
  }

  // Add guided methodologies if not disabled
  if (!settings.disableGuidedMethodologies) {
    const baseDelay = getMenuDelay(Math.max(methodologies.length - 1, 0));
    guidedMethodologies
      .filter(methodology => methodology.name !== "Basic Web Application Assessment")
      .forEach((methodology, index) => {
        const path = `/guided-methodologies/${methodology.id}?newSession=true`;
        menuItems.push({
          title: `Guided: ${methodology.name}`,
          delay: getMenuDelay(index, baseDelay),
          path
        });
      });
  }
  
  // Add RAWPA AI option if not disabled
  if (!settings.disableAI) {
    menuItems.push({ 
      title: "Ask RAWPA AI",
      delay: (methodologies.length + guidedMethodologies.length) * 0.1,
      icon: "/rawpa-ai-icon.svg",
      path: '/rawpa-ai'
    });
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <NavBar
        userName={userName}
        onSignOut={handleSignOut}
        inviteCount={inviteCount}
      />

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px]">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              {greeting}, {userName}!
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Quick Actions/Start New
            </p>
          </div>
          
          {areAllMethodologiesDisabled ? (
            <Alert className="mb-4 bg-blue-50 border-blue-200">
              <AlertTitle className="text-blue-800 font-semibold">All Methodologies Unavailable</AlertTitle>
              <AlertDescription className="text-blue-700">
                All methodologies are currently undergoing enhancements. Please check back later for updates.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {lastSession && (
                <Button 
                  className="w-full h-12 bg-[#daed6e] rounded-[20px] hover:bg-[#c5d85c] transition-colors flex items-center justify-center gap-2"
                  onClick={handleContinueSession}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                    Continue Previous Session
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </Button>
              )}
              
              <Button 
                className="w-full h-12 bg-[#daed6e] rounded-[20px] hover:bg-[#c5d85c] transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                disabled={menuItems.length === 0}
              >
                <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                  Start New Session
                </span>
              </Button>
              
              {/* AnimatePresence for menu items - positioned above Featured Methodology */}
              <AnimatePresence>
                {isMenuOpen && (
                  <div className="space-y-2 mt-4">
                    {menuItems.map((item) => (
                      <motion.button
                        key={item.title}
                        initial={{ x: 200, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -200, opacity: 0 }}
                        transition={{ 
                          duration: 0.3,
                          delay: item.delay,
                          ease: "easeOut"
                        }}
                        onClick={() => navigate(item.path)}
                        className="w-full h-12 bg-white rounded-[20px] flex items-center justify-center gap-2 shadow-md hover:bg-gray-50 transition-colors border border-[#E7E7E7]"
                      >
                        <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                          {item.title}
                        </span>
                        {item.icon && <img src={item.icon} alt="RAWPA AI" className="w-5 h-5" />}
                      </motion.button>
                    ))}
                  </div>
                )}
              </AnimatePresence>
              
              {/* RAWPA AI Button - only show if AI is not disabled */}
              {!settings.disableAI && (
                <Button 
                  className="w-full h-12 bg-white rounded-[20px] border border-[#daed6e] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  onClick={() => navigate('/rawpa-ai')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                    Ask RAWPA AI Assistant
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </Button>
              )}
              
              {/* Pentest Orchestrator Button - show independently of AI setting */}
              {!settings.disablePentestOrchestrator && (
                <Button 
                  className="w-full h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-[20px] hover:from-purple-600 hover:to-blue-700 transition-all text-white flex items-center justify-center gap-2 shadow-lg"
                  onClick={() => navigate('/pentest-orchestrator')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-white text-base">
                  Pentest Orchestrator
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </Button>
              )}
              
              {/* The Toolkits Button */}
              <Button 
                className="w-full h-12 bg-white rounded-[20px] border border-[#daed6e] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                onClick={() => navigate('/toolkits')}
              >
                <span className="font-['Space_Grotesk'] font-medium text-black text-base">The Toolkits
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </Button>
              
              {/* Hunter's Board Button */}
              <Button
                className="w-full h-12 bg-white rounded-[20px] border border-[#daed6e] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 relative"
                onClick={() => navigate('/hunters-board')}
              >
                <span className="font-['Space_Grotesk'] font-medium text-black text-base">Hunter's Board
                </span>
                {inviteCount > 0 && (
                  <span
                    className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center border-2 border-white shadow"
                    style={{ fontSize: '0.75rem', lineHeight: 1 }}
                    title="Pending Invitations"
                  >
                    {inviteCount}
                  </span>
                )}
              </Button>
              
              {/* Featured Methodology Section */}
              {settings.featuredMethodology && (
                <div className="mt-6 mb-2">
                  <h2 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
                    Featured Methodology
                  </h2>
                  <FeaturedMethodology
                    id={settings.featuredMethodology.id}
                    title={settings.featuredMethodology.title}
                    type={settings.featuredMethodology.type}
                    description={settings.featuredMethodology.description}
                  />
                </div>
              )}

              {/* Featured Toolkit Section */}
              {settings.featuredToolkit && (
                <div className="mt-6 mb-2">
                  <h2 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
                    Featured Toolkit
                  </h2>
                  <FeaturedToolkit
                    id={settings.featuredToolkit.id}
                    title={settings.featuredToolkit.title}
                    type={settings.featuredToolkit.type}
                    description={settings.featuredToolkit.description}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Search Dialog */}
      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby="search-dialog-description">
          <DialogHeader>
            <DialogTitle>Search Methodologies</DialogTitle>
            <DialogDescription id="search-dialog-description">
              Search for methodologies by title, description, or keywords.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Search by title, description, or keywords..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="mb-4"
              autoFocus
            />
            
            {searchQuery && searchResults.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No methodologies found matching your search.
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {searchResults.map((result) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    className="p-3 border rounded-md cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      navigate(result.path);
                      setIsSearchOpen(false);
                    }}
                  >
                    <div className="flex justify-between">
                      <h3 className="font-medium">{result.title}</h3>
                      <Badge variant={result.type === 'guided' ? 'outline' : 'secondary'}>
                        {result.type === 'guided' ? 'Guided' : 'Straight-up'}
                      </Badge>
                    </div>
                    {result.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{result.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};